import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { Formik } from 'formik';
import SubmitButton from './SubmitButton';
import Label from './Label';
import AddressLookup, { Address } from './AddressLookup';
import DayPickerInputValue from './edit/DayPickerInputValue';
import { CalendarIcon } from '@heroicons/react/24/solid';
import { useDateDisabler } from '../utilities/DateDisabler';
import { axiosKK } from '../services/networkRequest';
import {
  BoxData,
  ProductListResponse,
  OrderBoxesResponse,
  Customer,
} from '../models/APIModels';
import Loading from './Loading';

interface ONSProviderModalProps {
  isOpen: boolean;
  close: () => void;
  collectionAddress: string;
  deliveryAddress: string;
  orderBoxes?: OrderBoxesResponse[];
  orderId: number;
  customer?: Customer;
}

interface ONSFormValues {
  carrier: string;
  fromAddress: string;
  toAddress: string;
  date?: Date;
  boxes: { [key: number]: number };
  customFromAddress: Address;
  customToAddress: Address;
}

interface ONSFormErrors {
  carrier?: string;
  fromAddress?: string;
  toAddress?: string;
  date?: string;
  customFromAddress?: string;
  customToAddress?: string;
}

interface ShippingLabelResponse {
  labelId: string;
  trackingNumber: string;
  shipmentCost: number;
  labelDownload: {
    pdf: string;
    png: string;
    href: string;
  };
  carrierCode: string;
  serviceCode: string;
}

const customStyles = {
  content: {
    display: 'flex',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
};

export default function ONSProviderModal({
  isOpen,
  close,
  collectionAddress,
  deliveryAddress,
  orderBoxes,
  orderId,
  customer,
}: ONSProviderModalProps): JSX.Element {
  const [boxes, setBoxes] = useState<BoxData[]>([]);
  const [loadingBoxes, setLoadingBoxes] = useState(false);
  const [shippingLabelResponse, setShippingLabelResponse] =
    useState<ShippingLabelResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const disabledDays = useDateDisabler(null, null);

  const initializeBoxQuantities = (): { [key: number]: number } => {
    const quantities: { [key: number]: number } = {};
    if (orderBoxes) {
      orderBoxes.forEach((orderBox) => {
        if (orderBox.boxId) {
          quantities[orderBox.boxId] = (quantities[orderBox.boxId] || 0) + 1;
        }
      });
    }
    return quantities;
  };

  const getServiceCode = (carrier: string): string => {
    switch (carrier) {
      case 'dpd':
        return 'dpd_next_day';
      case 'parcelforce':
        return 'parcelforce_standard';
      default:
        return 'royal_mail_2nd_class';
    }
  };

  const initialValues: ONSFormValues = {
    carrier: '',
    fromAddress: '',
    toAddress: '',
    date: undefined,
    boxes: initializeBoxQuantities(),
    customFromAddress: {},
    customToAddress: {},
  };

  useEffect(() => {
    if (isOpen) {
      setLoadingBoxes(true);
      setError(null);
      setShippingLabelResponse(null);

      axiosKK
        .get<ProductListResponse>('/boxes')
        .then(({ data }) => {
          const shippableBoxes = data.products.filter(
            (product) => product.chargeShipping,
          );
          setBoxes(shippableBoxes);
        })
        .catch((error) => {
          console.error('Failed to fetch boxes:', error);
          setError('Failed to load box information');
        })
        .finally(() => {
          setLoadingBoxes(false);
        });
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={close}
      style={customStyles}
      contentLabel="ONS Provider Modal"
    >
      <Formik
        initialValues={initialValues}
        validate={(values) => {
          const errors: ONSFormErrors = {};

          if (!values.carrier) {
            errors.carrier = 'Please select a carrier';
          }

          if (!values.fromAddress) {
            errors.fromAddress = 'Please select a from address';
          }

          if (!values.toAddress) {
            errors.toAddress = 'Please select a to address';
          }

          if (!values.date) {
            errors.date = 'Please select a date';
          }

          if (
            values.fromAddress === 'custom' &&
            (!values.customFromAddress.line1 ||
              !values.customFromAddress.postCode)
          ) {
            errors.customFromAddress =
              'Please enter a valid custom from address';
          }

          if (
            values.toAddress === 'custom' &&
            (!values.customToAddress.line1 || !values.customToAddress.postCode)
          ) {
            errors.customToAddress = 'Please enter a valid custom to address';
          }

          return errors;
        }}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);
          setError(null);

          const parseAddressString = (addressString: string) => {
            const parts = addressString
              .split(', ')
              .filter((part) => part.trim());
            return {
              line1: parts[0] || '',
              line2: parts[1] || '',
              city: parts[parts.length - 3] || '',
              postCode: parts[parts.length - 2] || '',
              country: 'GB',
            };
          };

          const getFromAddress = () => {
            if (values.fromAddress === 'custom') {
              return {
                name: 'Kit Keeper Warehouse',
                phone: '01234567890',
                line1: values.customFromAddress.line1 || '',
                line2: values.customFromAddress.line2 || '',
                city: values.customFromAddress.city || '',
                postCode: values.customFromAddress.postCode || '',
                country: 'GB',
              };
            } else {
              const addressString =
                values.fromAddress === 'collection'
                  ? collectionAddress
                  : deliveryAddress;
              const parsed = parseAddressString(addressString);
              return {
                name: 'Kit Keeper Warehouse',
                phone: '01234567890',
                ...parsed,
              };
            }
          };

          const getToAddress = () => {
            if (values.toAddress === 'custom') {
              return {
                name:
                  `${customer?.firstName || ''} ${
                    customer?.lastName || ''
                  }`.trim() || 'Customer',
                phone: customer?.phoneNumber || '07000000000',
                line1: values.customToAddress.line1 || '',
                line2: values.customToAddress.line2 || '',
                city: values.customToAddress.city || '',
                postCode: values.customToAddress.postCode || '',
                country: 'GB',
              };
            } else {
              const addressString =
                values.toAddress === 'collection'
                  ? collectionAddress
                  : deliveryAddress;
              const parsed = parseAddressString(addressString);
              return {
                name:
                  `${customer?.firstName || ''} ${
                    customer?.lastName || ''
                  }`.trim() || 'Customer',
                phone: customer?.phoneNumber || '07000000000',
                ...parsed,
              };
            }
          };

          const requestData = {
            carrierId: values.carrier,
            serviceCode: getServiceCode(values.carrier),
            fromAddress: getFromAddress(),
            toAddress: getToAddress(),
            date: values.date,
          };

          axiosKK
            .post<ShippingLabelResponse>(
              `/order/${orderId}/shipping`,
              requestData,
            )
            .then(({ data }) => {
              setShippingLabelResponse(data);
              setSubmitting(false);
            })
            .catch((error) => {
              console.error('Failed to create shipping label:', error);
              setError(
                error.response?.data?.error ||
                  'Failed to create shipping label',
              );
              setSubmitting(false);
            });
        }}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          errors,
          touched,
          isSubmitting,
          isValid,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit} className="ons-provider-form">
            <div className="modal-header">
              <h3>ONS Provider Details</h3>
              <SubmitButton red={true} type="button" onClick={close}>
                Close
              </SubmitButton>
            </div>

            <div className="modal-content">
              {loadingBoxes && (
                <div className="loading-message">
                  <p>Loading box information...</p>
                  <Loading />
                </div>
              )}

              {shippingLabelResponse && (
                <div className="shipping-label-success">
                  <h4>Shipping Label Created Successfully!</h4>
                  <div className="label-details">
                    <p>
                      <strong>Tracking Number:</strong>{' '}
                      {shippingLabelResponse.trackingNumber}
                    </p>
                    <p>
                      <strong>Label ID:</strong> {shippingLabelResponse.labelId}
                    </p>
                    <p>
                      <strong>Cost:</strong> £
                      {shippingLabelResponse.shipmentCost.toFixed(2)}
                    </p>
                    <p>
                      <strong>Carrier:</strong>{' '}
                      {shippingLabelResponse.carrierCode}
                    </p>
                    <p>
                      <strong>Service:</strong>{' '}
                      {shippingLabelResponse.serviceCode}
                    </p>
                  </div>
                  <div className="label-downloads">
                    <a
                      href={shippingLabelResponse.labelDownload.pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <SubmitButton type="button">Download PDF</SubmitButton>
                    </a>
                    <a
                      href={shippingLabelResponse.labelDownload.png}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <SubmitButton type="button">Download PNG</SubmitButton>
                    </a>
                  </div>
                </div>
              )}

              {error && (
                <div className="error-message">
                  <span className="error">{error}</span>
                </div>
              )}

              {!shippingLabelResponse && !loadingBoxes && (
                <>
                  <div className="form-group">
                    <Label htmlFor="carrier">Carrier</Label>
                    <select
                      id="carrier"
                      name="carrier"
                      value={values.carrier}
                      onChange={handleChange}
                      className="form-select"
                    >
                      <option value="">Select a carrier</option>
                      <option value="dpd">DPD</option>
                      <option value="parcelforce">ParcelForce</option>
                    </select>
                    {touched.carrier && errors.carrier && (
                      <span className="error">{errors.carrier}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <Label htmlFor="fromAddress">From Address</Label>
                    <select
                      id="fromAddress"
                      name="fromAddress"
                      value={values.fromAddress}
                      onChange={handleChange}
                      className="form-select"
                    >
                      <option value="">Select from address</option>
                      <option value="collection">Collection Address</option>
                      <option value="delivery">Delivery Address</option>
                      <option value="custom">Custom Address</option>
                    </select>
                    {touched.fromAddress && errors.fromAddress && (
                      <span className="error">{errors.fromAddress}</span>
                    )}

                    {values.fromAddress && values.fromAddress !== 'custom' && (
                      <div className="address-display">
                        <p className="grey">
                          {values.fromAddress === 'collection'
                            ? collectionAddress
                            : deliveryAddress}
                        </p>
                      </div>
                    )}

                    {values.fromAddress === 'custom' && (
                      <div className="custom-address-input">
                        <AddressLookup
                          address={values.customFromAddress}
                          setAddress={(address: Address) => {
                            setFieldValue('customFromAddress', address);
                          }}
                          onlyUnitedKingdom
                          idPrefix="ons-from"
                        />
                        {touched.customFromAddress &&
                          errors.customFromAddress && (
                            <span className="error">
                              {errors.customFromAddress}
                            </span>
                          )}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <Label htmlFor="toAddress">To Address</Label>
                    <select
                      id="toAddress"
                      name="toAddress"
                      value={values.toAddress}
                      onChange={handleChange}
                      className="form-select"
                    >
                      <option value="">Select to address</option>
                      <option value="collection">Collection Address</option>
                      <option value="delivery">Delivery Address</option>
                      <option value="custom">Custom Address</option>
                    </select>
                    {touched.toAddress && errors.toAddress && (
                      <span className="error">{errors.toAddress}</span>
                    )}

                    {values.toAddress && values.toAddress !== 'custom' && (
                      <div className="address-display">
                        <p className="grey">
                          {values.toAddress === 'collection'
                            ? collectionAddress
                            : deliveryAddress}
                        </p>
                      </div>
                    )}

                    {values.toAddress === 'custom' && (
                      <div className="custom-address-input">
                        <AddressLookup
                          address={values.customToAddress}
                          setAddress={(address: Address) => {
                            setFieldValue('customToAddress', address);
                          }}
                          onlyUnitedKingdom
                          idPrefix="ons-to"
                        />
                        {touched.customToAddress && errors.customToAddress && (
                          <span className="error">
                            {errors.customToAddress}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <div className="title-and-input">
                      <div className="title">
                        <Label>Shipping Date:</Label>
                        <CalendarIcon
                          style={{
                            width: '30px',
                            height: '30px',
                            color: '#6c3cd9',
                          }}
                        />
                      </div>
                      <DayPickerInputValue
                        id="onsDate"
                        handleStateChange={(day) => setFieldValue('date', day)}
                        disabled={false}
                        disabledDays={disabledDays}
                      />
                    </div>
                    {touched.date && errors.date && (
                      <span className="error">{errors.date}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <Label>Box Quantities</Label>
                    <div className="box-quantities">
                      {boxes.map((box) => (
                        <div key={box.id} className="box-quantity-row">
                          <div className="box-info">
                            <span className="box-name">{box.name}</span>
                            <span className="box-description">
                              {box.description}
                            </span>
                          </div>
                          <input
                            type="number"
                            min="0"
                            value={values.boxes[box.id] || 0}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 0;
                              setFieldValue(`boxes.${box.id}`, value);
                            }}
                            className="quantity-input"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              {shippingLabelResponse ? (
                <SubmitButton type="button" onClick={close}>
                  Close
                </SubmitButton>
              ) : (
                <SubmitButton disabled={!isValid} isSubmitting={isSubmitting}>
                  Create Shipping Label
                </SubmitButton>
              )}
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
}
